import clsx from 'clsx'
import { PaginationState } from '../models'
import { FC } from 'react'
import { useIntl } from 'react-intl'

type PaginationProps = {
  pagination: PaginationState
  isLoading: boolean
  updateState: (updates: Partial<any>) => void
}

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const

const Pagination: FC<PaginationProps> = ({ pagination, isLoading, updateState }) => {
  const intl = useIntl()
  const currentSize = pagination.pageSize || 10

  const updatePage = (page: number | null) => {
    if (page === null || isLoading || pagination.currentPage === page || page < 0 || page >= pagination.totalPages) {
      return
    }
    updateState({ page, size: currentSize })
  }

  const handlePageSizeChange = (value: string) => {
    if (value === 'all') {
      updateState({ page: 0, size: pagination.totalItems || currentSize })
      return
    }
    updateState({ page: 0, size: parseInt(value, 10) })
  }

  const renderPageNumbers = () => {
    const { currentPage, totalPages } = pagination
    const pages = []

    const startPage = Math.max(0, currentPage - 1)
    const endPage = Math.min(totalPages - 1, currentPage + 1)

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={clsx('page-item', { active: currentPage === i })}>
          <a className="page-link" onClick={() => updatePage(i)} style={{ cursor: 'pointer' }}>
            {i + 1}
          </a>
        </li>
      )
    }

    if (endPage < totalPages - 2) {
      pages.push(
        <li key="ellipsis-end" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      )
    }

    if (endPage < totalPages - 1) {
      pages.push(
        <li key={totalPages - 1} className={clsx('page-item', { active: currentPage === totalPages - 1 })}>
          <a className="page-link" onClick={() => updatePage(totalPages - 1)} style={{ cursor: 'pointer' }}>
            {totalPages}
          </a>
        </li>
      )
    }

    return pages
  }

  const isAllSelected = currentSize >= pagination.totalItems && pagination.totalItems > 0

  return (
    <div className="row">
      <div className="col-sm-12 col-md-5 d-flex align-items-center justify-content-center justify-content-md-start">
        <div className="d-flex align-items-center gap-2">
          <label className="text-muted fs-7 mb-0">
            {intl.formatMessage({ id: 'PAGINATION.ROWS_PER_PAGE', defaultMessage: 'Lignes par page' })}
          </label>
          <select
            className="form-select form-select-sm w-auto"
            value={isAllSelected ? 'all' : currentSize}
            disabled={isLoading}
            onChange={(e) => handlePageSizeChange(e.target.value)}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
            <option value="all">
              {intl.formatMessage({ id: 'PAGINATION.ALL', defaultMessage: 'Toutes' })}
            </option>
          </select>
        </div>
      </div>
      <div className="col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end">
        <div id="kt_table_users_paginate">
          <ul className="pagination">
            {pagination.currentPage > 0 && (
              <li className={clsx('page-item', { disabled: isLoading })}>
                <a
                  className="page-link page-text me-5"
                  onClick={() => updatePage(pagination.currentPage - 1)}
                  style={{ cursor: 'pointer' }}
                >
                  {intl.formatMessage({ id: 'ADMIN.PREVIOUS' })}
                </a>
              </li>
            )}
            {renderPageNumbers()}
            {pagination.currentPage < pagination.totalPages - 1 && (
              <li className={clsx('page-item', { disabled: isLoading })}>
                <a
                  className="page-link page-text"
                  onClick={() => updatePage(pagination.currentPage + 1)}
                  style={{ cursor: 'pointer' }}
                >
                  {intl.formatMessage({ id: 'ADMIN.NEXT' })}
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  )
}

export { Pagination }
